// @flow
import React from 'react';
import { useSelector } from 'react-redux';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';

import * as preferencesSelectors from 'companion-app-components/flux/preferences/selectors';
import { getCurrentThemePreferenceKey } from 'themes/themesSelectors';
import { getThemeByKey } from 'themes/themeBase';

type Props = {
  children: React.Node,
};

const getOsThemeKey = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const CombinedProviders = ({ children } : Props) => {
  const themePreferenceKey = useSelector(getCurrentThemePreferenceKey);
  const preferencesLoading = useSelector(preferencesSelectors.getLoadPendingState);
  const [osThemeKey, setOsThemeKey] = React.useState(getOsThemeKey);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia || themePreferenceKey !== 'system') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = () => {
      setOsThemeKey(mediaQuery.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    }

    mediaQuery.addListener(handleThemeChange);
    return () => mediaQuery.removeListener(handleThemeChange);
  }, [themePreferenceKey]);

  const theme = getThemeByKey(themePreferenceKey === 'system' ? osThemeKey : themePreferenceKey);

  // Overwrite the inline styles set by the EJS FOUC-prevention script
  React.useLayoutEffect(() => {
    const bgColor = theme.palette.background.default;
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
  }, [theme.palette.mode, theme.palette.background.default]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || preferencesLoading?.shared?.user) {
      return;
    }

    window.localStorage?.setItem('theme', themePreferenceKey);
  }, [preferencesLoading, themePreferenceKey]);

  return (
    <StyledEngineProvider injectFirst>
      <EmotionThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <StyledComponentsThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              {children}
            </LocalizationProvider>
          </StyledComponentsThemeProvider>
        </ThemeProvider>
      </EmotionThemeProvider>
    </StyledEngineProvider>
  );

};

export default CombinedProviders;
